import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectedOptions } from '@makro-shopping/product-option-selector';
import { OtherProduct } from '@makro-shopping/use-product-card-logic/dist/types/product.type';
import { config } from '../config/env';
import { ANALYTICS } from '../constants/analytics';
import { useStaticData } from '../contexts/DataProvider.context';
import SendBrowserEvent from '../services/sendBrowserEvent.service';
import { CarouselAnalyticsProps } from '../types/carousel.types';
import { Product } from '../types/product.types';
import { findPreviewBasketItemByVariantOrProductId } from '../utils/basket.util';
import { getSlabPriceValue } from '../utils/getSlabPriceDiscount.util';
import { getProductAvailability, isFlagEnabledForCountry } from '../utils/helper';
import { useBasket } from './useBasket';

export interface UseProductOptionSelectorProps {
  productData?: Product;
  productCount?: number;
  onAddToCartSuccess?: (selectedOptions: SelectedOptions) => void;
  onVariantChange?: (variant: OtherProduct | null) => void;
  onFetchVariantProduct?: (variantProductId: string) => Promise<Product | null>;
  onModalOpen?: () => void;
  onModalClose?: () => void;
  onModalDelete?: (isLoggedIn: boolean) => void;
  onDeleteWithProductId?: (productId: string) => Promise<void>;
  isLoggedIn?: boolean;
  handleDecrement?: (e: SyntheticEvent) => void;
  formattedValue?: number;
  minimumQuantity?: number;
  resetValue?: () => void;
  showKg?: boolean;
  moq?: number;
  isPDP?: boolean;
  analyticsProps?: CarouselAnalyticsProps;
  isHorizonUiUxEnabled?: boolean;
}

export interface UseProductOptionSelectorReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;

  selectedOptions: SelectedOptions;
  handleOptionSelect: (optionName: string, value: string) => void;
  resetSelectedOptions: () => void;

  handleOptionClick: () => void;
  handleOverflowClick: () => void;

  handleAddToCart: () => void;
  handleModalDelete: () => void;
  modalHandleDecrement: (e: SyntheticEvent) => void;

  allOptionsSelected: boolean;

  selectedVariant: OtherProduct | null;

  getSelectedProductId: () => string;

  selectedProductBasketQuantity: number;

  totalBasketQuantity: number;

  variantPrice: number | null;
  variantOriginPrice: number | null;
  variantMoq: number | null;
  selectedVariantProduct: Product | null;
  isLoadingVariantPrice: boolean;

  productOptionSelectorProps: {
    open: boolean;
    onClose: () => void;
    productImageUrl: string | undefined;
    productPrice: string;
    productCurrency: string;
    options: Product['options'];
    selectedOptions: SelectedOptions;
    onOptionSelect: (optionName: string, value: string) => void;
    totalCurrency: string;
    onAddToCart: () => void;
    addToCartDisabled: boolean;
    optionLabel?: string;
    moq?: number;
  };

  isSelectedVariantInStock: boolean;
  selectedVariantInventory: number;
}

const findMatchingVariant = (
  otherProducts: OtherProduct[] | undefined,
  selectedOptions: SelectedOptions,
): OtherProduct | null => {
  if (!otherProducts || otherProducts.length === 0) return null;

  const matchingVariant = otherProducts.find((variant) => {
    const variantOptions = variant.optionValues;
    return Object.entries(selectedOptions).every(([key, value]) => {
      return variantOptions[key] === value;
    });
  });

  return matchingVariant || null;
};

export const useProductOptionSelector = (props?: UseProductOptionSelectorProps): UseProductOptionSelectorReturn => {
  const {
    productData,
    onAddToCartSuccess,
    onVariantChange,
    onFetchVariantProduct,
    onModalOpen,
    onModalClose,
    onModalDelete,
    onDeleteWithProductId,
    isLoggedIn = false,
    handleDecrement,
    formattedValue = 0,
    minimumQuantity = 1,
    resetValue,
    analyticsProps,
    isHorizonUiUxEnabled,
  } = props || {};

  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>(() => {
    return productData?.optionValues || {};
  });

  const [selectedVariantProduct, setSelectedVariantProduct] = useState<Product | null>(null);
  const [isLoadingVariantPrice, setIsLoadingVariantPrice] = useState(false);
  const fetchVariantPriceAbortRef = useRef<number>(0);
  const { currentStoreCodes, alcoholExpiryDate, countryCode } = useStaticData();
  const { isAlcoholProhibited, previewBasketItems } = useBasket();
  const hasAlcoholProductAvailability = isFlagEnabledForCountry(countryCode, config.ALCOHOL_PRODUCT_AVAILABILITY);

  const variantPrice = useMemo(() => {
    if (!selectedVariantProduct) return null;

    const variantMoq = selectedVariantProduct.moq || 1;

    const variantShowKg = !!selectedVariantProduct.isWeightedFresh;
    const shouldDivideByMoq = variantShowKg || variantMoq > 1;
    const rawPrice = selectedVariantProduct.displayPrice ?? null;
    if (rawPrice === null) return null;
    const slabTiers = selectedVariantProduct.slabPrices?.slabPriceTiers || [];

    if (slabTiers.length > 0) {
      const { priceValue } = getSlabPriceValue(
        'PDP_POPUP',
        slabTiers,
        formattedValue || minimumQuantity || variantMoq,
        rawPrice,
      );
      return shouldDivideByMoq ? priceValue / variantMoq : priceValue;
    }
    return shouldDivideByMoq ? rawPrice / variantMoq : rawPrice;
  }, [selectedVariantProduct, formattedValue, minimumQuantity]);

  const variantOriginPrice = useMemo(() => {
    if (!selectedVariantProduct) return null;
    const variantMoq = selectedVariantProduct.moq || 1;
    const variantShowKg = !!selectedVariantProduct.isWeightedFresh;
    const shouldDivideByMoq = variantShowKg || variantMoq > 1;
    const rawPrice = selectedVariantProduct.originPrice ?? null;
    if (rawPrice === null) return null;
    return shouldDivideByMoq ? rawPrice / variantMoq : rawPrice;
  }, [selectedVariantProduct]);

  const variantAvailability = useMemo(() => {
    if (!selectedVariantProduct) return null;
    return getProductAvailability(
      selectedVariantProduct,
      currentStoreCodes,
      { alcoholExpiryDate: alcoholExpiryDate },
      undefined,
      isAlcoholProhibited,
      countryCode,
      hasAlcoholProductAvailability,
    );
  }, [
    selectedVariantProduct,
    currentStoreCodes,
    alcoholExpiryDate,
    isAlcoholProhibited,
    countryCode,
    hasAlcoholProductAvailability,
  ]);

  const isInitialMount = useRef(true);
  const previousVariantRef = useRef<OtherProduct | null>(null);
  const isUserSelectionRef = useRef(false);

  const previousOptionValuesRef = useRef<string>(JSON.stringify(productData?.optionValues || {}));

  useEffect(() => {
    if (productData?.optionValues) {
      const currentOptionValuesStr = JSON.stringify(productData.optionValues);

      if (previousOptionValuesRef.current !== currentOptionValuesStr) {
        previousOptionValuesRef.current = currentOptionValuesStr;
        setSelectedOptions(productData.optionValues);
      }
    }
  }, [productData?.optionValues]);

  const selectedVariant = useMemo(() => {
    return findMatchingVariant(productData?.otherProducts, selectedOptions);
  }, [productData?.otherProducts, selectedOptions]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousVariantRef.current = selectedVariant;
      return;
    }

    const prevVariantId = previousVariantRef.current?.shopifyProductId;
    const currentVariantId = selectedVariant?.shopifyProductId;

    if (prevVariantId !== currentVariantId) {
      previousVariantRef.current = selectedVariant;
      onVariantChange?.(selectedVariant);

      // Only fetch variant product data when triggered by user option selection
      if (isUserSelectionRef.current && selectedVariant?.shopifyProductId && onFetchVariantProduct) {
        const fetchId = ++fetchVariantPriceAbortRef.current;
        setIsLoadingVariantPrice(true);
        onFetchVariantProduct(selectedVariant.shopifyProductId)
          .then((product) => {
            if (fetchId === fetchVariantPriceAbortRef.current) {
              setSelectedVariantProduct(product);
            }
          })
          .catch(() => {
            if (fetchId === fetchVariantPriceAbortRef.current) {
              setSelectedVariantProduct(null);
            }
          })
          .finally(() => {
            if (fetchId === fetchVariantPriceAbortRef.current) {
              setIsLoadingVariantPrice(false);
            }
          });
      } else {
        setSelectedVariantProduct(null);
        setIsLoadingVariantPrice(false);
      }

      isUserSelectionRef.current = false;
    }
  }, [selectedVariant, onVariantChange, onFetchVariantProduct]);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    // Blur the focused element before closing to avoid aria-hidden accessibility warning
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setIsOpen(false);
    // Reset selected options to product's original values when modal is closed
    setSelectedOptions(productData?.optionValues || {});
    setSelectedVariantProduct(null);
    setIsLoadingVariantPrice(false);
    // Reset quantity picker value
    resetValue?.();
    // Call onModalClose callback
    onModalClose?.();
    if (!isHorizonUiUxEnabled) {
      SendBrowserEvent.setIframeHeight(false, false);
    }
  }, [productData?.optionValues, resetValue, onModalClose, isHorizonUiUxEnabled]);

  const handleOptionClick = useCallback(() => {
    onModalOpen?.();
    setIsOpen(true);
    // const eventName = isPDP
    //   ? (AnalyticsEvents.Shopping as any).PRODUCT_OPTIONS_VIEWED_PDP
    //   : (AnalyticsEvents.Shopping as any).PRODUCT_OPTIONS_VIEWED_PLP;
    // analyticSDK.trackEvent(eventName as any, {
    //   productId: productData?.id,
    //   sku: productData?.sku,
    //   productPrice: productData?.displayPrice,
    //   sourceEvent: isPDP ? 'PDP' : 'PLP',
    // });
    SendBrowserEvent.setIframeHeight(isHorizonUiUxEnabled, true);
  }, [onModalOpen, isHorizonUiUxEnabled]);

  const handleOverflowClick = useCallback(() => {
    onModalOpen?.();
    setIsOpen(true);
  }, [onModalOpen]);

  // When a parent option is changed, clear all child options (options that come after it)
  const handleOptionSelect = useCallback(
    (optionName: string, value: string) => {
      isUserSelectionRef.current = true;
      const options = productData?.options || [];
      const changedOptionIndex = options.findIndex((opt) => opt.name === optionName);

      let trackedOptions: string | undefined;

      setSelectedOptions((prev) => {
        const newSelectedOptions: SelectedOptions = {};

        options.forEach((opt, index) => {
          if (index < changedOptionIndex && prev[opt.name]) {
            newSelectedOptions[opt.name] = prev[opt.name];
          } else if (index === changedOptionIndex) {
            newSelectedOptions[opt.name] = value;
          }
        });

        trackedOptions = JSON.stringify(newSelectedOptions);
        SendBrowserEvent.triggerProductOptionSelectorAnalytics(ANALYTICS.PRODUCT_OPTION_SELECTOR_SELECTED, {
          selectedOptions: trackedOptions,
          productId: productData?.id,
          productPrice: productData?.displayPrice,
          sku: productData?.sku || productData?.variants?.[0]?.sku || '',
          makroId: productData?.makroId || '',
          ...analyticsProps,
        });
        return newSelectedOptions;
      });
    },
    [productData, analyticsProps],
  );

  const resetSelectedOptions = useCallback(() => {
    setSelectedOptions(productData?.optionValues || {});
  }, [productData?.optionValues]);

  const handleAddToCart = useCallback(() => {
    onAddToCartSuccess?.(selectedOptions);
    setIsOpen(false);
  }, [selectedOptions, onAddToCartSuccess]);

  // Function to get the current selected product ID (dynamically computed)
  const getSelectedProductId = useCallback(() => {
    const currentVariant = findMatchingVariant(productData?.otherProducts, selectedOptions);
    return currentVariant?.shopifyProductId || productData?.id || '';
  }, [productData?.otherProducts, productData?.id, selectedOptions]);

  const handleModalDelete = useCallback(async () => {
    if (isLoggedIn && onDeleteWithProductId) {
      const productId = getSelectedProductId();
      if (productId) {
        await onDeleteWithProductId(productId);
      }
    }
    onModalDelete?.(isLoggedIn);
    setIsOpen(false);
  }, [onModalDelete, onDeleteWithProductId, isLoggedIn, getSelectedProductId]);

  const modalHandleDecrement = useCallback(
    (e: SyntheticEvent) => {
      const isAtMinQuantity = formattedValue <= minimumQuantity;

      if (isAtMinQuantity) {
        if (isLoggedIn) {
          handleDecrement?.(e);
        }

        setIsOpen(false);
      } else {
        handleDecrement?.(e);
      }
    },
    [formattedValue, minimumQuantity, isLoggedIn, handleDecrement],
  );

  const allOptionsSelected = useMemo(() => {
    const options = productData?.options || [];
    if (options.length === 0) return true;
    return options.every((option) => {
      const selectedValue = selectedOptions[option.name];
      return selectedValue !== undefined && selectedValue !== '';
    });
  }, [productData?.options, selectedOptions]);

  const currentImageUrl = useMemo(() => {
    if (selectedVariant?.imageUrl) {
      return selectedVariant.imageUrl;
    }
    return productData?.imageUrls?.[0];
  }, [selectedVariant, productData?.imageUrls]);

  // Collect all available option combinations (current product + otherProducts)
  // Only includes combinations that are in stock
  const allAvailableCombinations = useMemo(() => {
    const combinations: Array<Record<string, string>> = [];

    if (productData?.optionValues) {
      const status = productData.status?.toLowerCase();
      const isMainActive = status === undefined || status === 'active';
      const mainInventory = productData.totalInventory || 0;
      if (isMainActive && mainInventory > 0) {
        combinations.push(productData.optionValues);
      }
    }

    if (productData?.otherProducts) {
      productData.otherProducts.forEach((product) => {
        if (product.optionValues && product.masterStatus === 'active') {
          const hasInventory = product.variants?.some(
            (v) => v.variantStatus?.toLowerCase() === 'active' && v.inventory > 0,
          );
          if (hasInventory) {
            combinations.push(product.optionValues);
          }
        }
      });
    }

    return combinations;
  }, [productData?.optionValues, productData?.otherProducts, productData?.status, productData?.totalInventory]);

  // Check if the currently selected variant is in stock
  const isSelectedVariantInStock = useMemo(() => {
    if (!allOptionsSelected) return false;

    if (variantAvailability) {
      return variantAvailability.isAvailable;
    }

    return allAvailableCombinations.some((combination) => {
      return Object.entries(selectedOptions).every(([key, val]) => {
        return combination[key] === val;
      });
    });
  }, [allOptionsSelected, allAvailableCombinations, selectedOptions, variantAvailability]);

  // Calculate the inventory of the selected variant
  const selectedVariantInventory = useMemo(() => {
    // If no variant is selected or if main product is selected, use main product inventory
    if (!selectedVariant) {
      return productData?.totalInventory || 0;
    }

    const totalVariantInventory =
      selectedVariant.variants?.reduce((sum, v) => {
        if (v.variantStatus?.toLowerCase() === 'active') {
          return sum + (v.inventory || 0);
        }
        return sum;
      }, 0) || 0;

    return totalVariantInventory;
  }, [selectedVariant, productData?.totalInventory]);

  // Compute options with disabled state based on available combinations
  // Uses hierarchical logic: first option shows all available values,
  // subsequent options filter based on previous selections
  const optionsWithDisabledState = useMemo(() => {
    const options = productData?.options;
    if (!options || options.length === 0) return options;

    return options.map((option, optionIndex) => {
      const optionName = option.name;

      const valuesWithDisabled = option.values.map((value) => {
        const previousSelections: Record<string, string> = {};
        for (let i = 0; i < optionIndex; i++) {
          const prevOptionName = options[i].name;
          if (selectedOptions[prevOptionName]) {
            previousSelections[prevOptionName] = selectedOptions[prevOptionName];
          }
        }

        const hypotheticalSelection = {
          ...previousSelections,
          [optionName]: value.label,
        };

        const isAvailable = allAvailableCombinations.some((combination) => {
          return Object.entries(hypotheticalSelection).every(([key, val]) => {
            if (combination[key] !== undefined) {
              return combination[key] === val;
            }
            return true;
          });
        });

        const isLastOption = optionIndex === options.length - 1;
        let inCartQuantity = 0;

        if (isLastOption) {
          if (productData?.optionValues && productData?.id) {
            const matchesCurrent = Object.entries(hypotheticalSelection).every(([key, val]) => {
              return productData.optionValues?.[key] === val;
            });
            if (matchesCurrent) {
              const basketItem = findPreviewBasketItemByVariantOrProductId(
                previewBasketItems,
                undefined,
                productData.id,
              );
              inCartQuantity += basketItem?.quantity || 0;
            }
          }

          if (productData?.otherProducts) {
            productData.otherProducts.forEach((otherProduct) => {
              if (otherProduct.optionValues && otherProduct.masterStatus === 'active') {
                const matchesOther = Object.entries(hypotheticalSelection).every(([key, val]) => {
                  return otherProduct.optionValues[key] === val;
                });
                if (matchesOther && otherProduct.shopifyProductId) {
                  const basketItem = findPreviewBasketItemByVariantOrProductId(
                    previewBasketItems,
                    undefined,
                    otherProduct.shopifyProductId,
                  );
                  inCartQuantity += basketItem?.quantity || 0;
                }
              }
            });
          }
        }

        return {
          ...value,
          disabled: !isAvailable,
          inCart: inCartQuantity > 0 ? inCartQuantity : undefined,
        };
      });

      return {
        ...option,
        values: valuesWithDisabled,
      };
    });
  }, [
    productData?.options,
    productData?.optionValues,
    productData?.otherProducts,
    productData?.id,
    selectedOptions,
    allAvailableCombinations,
    previewBasketItems,
  ]);

  const variantMoq = useMemo(() => selectedVariantProduct?.moq ?? null, [selectedVariantProduct]);

  const selectedProductBasketQuantity = useMemo(() => {
    const currentProductId = getSelectedProductId();
    if (!currentProductId || !previewBasketItems) return 0;

    const basketItem = findPreviewBasketItemByVariantOrProductId(previewBasketItems, undefined, currentProductId);

    return basketItem?.quantity || 0;
  }, [getSelectedProductId, previewBasketItems]);

  const productOptionSelectorProps = useMemo(
    () => ({
      open: isOpen,
      onClose: close,
      productImageUrl: currentImageUrl,
      productPrice: (variantPrice ?? productData?.displayPrice)?.toString() || '0.00',
      productCurrency: productData?.priceUnit || '',
      options: optionsWithDisabledState,
      selectedOptions,
      onOptionSelect: handleOptionSelect,
      totalCurrency: productData?.priceUnit || '',
      onAddToCart: handleAddToCart,
      addToCartDisabled:
        !allOptionsSelected || !isSelectedVariantInStock || (variantAvailability?.isRestricted ?? false),
      addToCartButtonText: selectedProductBasketQuantity > 0 ? t('variants.updateToBasket') : t('variants.addToBasket'),
      quantityLabel: t('variants.quantity'),
      selectVariantsLabel: t('variants.selectVariants'),
      optionLabel: t('variants.option'),
      moq: productData?.moq,
    }),
    [
      isOpen,
      close,
      currentImageUrl,
      variantPrice,
      productData?.displayPrice,
      productData?.priceUnit,
      optionsWithDisabledState,
      selectedOptions,
      handleOptionSelect,
      handleAddToCart,
      allOptionsSelected,
      isSelectedVariantInStock,
      variantAvailability,
      selectedProductBasketQuantity,
      t,
      productData?.moq,
    ],
  );

  // Calculate total basket quantity across all variants of this product
  const totalBasketQuantity = useMemo(() => {
    if (!previewBasketItems || !productData?.id) return 0;

    const mainProductItem = findPreviewBasketItemByVariantOrProductId(previewBasketItems, undefined, productData.id);
    const mainProductQty = mainProductItem?.quantity || mainProductItem?.quantitySetByUser || 0;

    let total = mainProductQty;
    if (productData.otherProducts) {
      productData.otherProducts.forEach((variant) => {
        if (variant.shopifyProductId) {
          const variantItem = findPreviewBasketItemByVariantOrProductId(
            previewBasketItems,
            undefined,
            variant.shopifyProductId,
          );
          total += variantItem?.quantity || variantItem?.quantitySetByUser || 0;
        }
      });
    }

    return total;
  }, [previewBasketItems, productData?.id, productData?.otherProducts]);

  return {
    isOpen,
    open,
    close,
    selectedOptions,
    handleOptionSelect,
    resetSelectedOptions,
    handleOptionClick,
    handleOverflowClick,
    handleAddToCart,
    handleModalDelete,
    modalHandleDecrement,
    allOptionsSelected,
    selectedVariant,
    getSelectedProductId,
    selectedProductBasketQuantity,
    totalBasketQuantity,
    variantPrice,
    variantOriginPrice,
    variantMoq,
    selectedVariantProduct,
    isLoadingVariantPrice,
    isSelectedVariantInStock,
    selectedVariantInventory,
    productOptionSelectorProps,
  };
};
